import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import {
  PolarisObject,
  Role,
  TaskKind,
  User,
} from "../../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../../app/client";
import { useAppSelector } from "../../../../app/hooks";
import UserSpan from "../../users/UserSpan";
import Morselbar from "../../Morselbar";

export default function PolarisGroupAccessDialog(props: {
  open: boolean;
  setOpen: (val: boolean) => void;
  user: User;
  group: PolarisObject;
  callback: () => void;
}) {
  const { open, setOpen, user, group, callback } = props;

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [taskNote, setTaskNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createJiraProjectAccessTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = taskNote || undefined;

    const body: TaskKind = {
      kind: "PolarisGroupAccess",
      inner: {
        target: user.employee.id,
        group_id: group.id || "",
        group_name: group.attributes.groupname || "",
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => {
        setOpen(false);
        callback();
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    taskNote,
    user.employee.id,
    group.id,
    group.attributes.groupname,
    client,
    override,
    setOpen,
    callback,
  ]);

  return (
    <>
      <Dialog open={open}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              <UserSpan user={user} /> will be assigned to{" "}
              <Typography component="span" color="secondary">
                {group.attributes.groupname}
              </Typography>
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <br />

          <TextField
            onChange={(e) => setTaskNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={override}
                        onChange={(e) => setOverride(e.target.checked)}
                      />
                    }
                    label="Skip approvals"
                  />
                </>
              )}
            </Box>

            <Button
              onClick={() => createJiraProjectAccessTask()}
              disabled={loading}
              color="success"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}
